import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StoreEndpointsConstant } from '@constants/store-endpoints.constant';
import { Notification, NotificationResponse } from '@models/notifications.model';
import { StripHtmlTagsPipe } from '@pipes/strip-html-tags/strip-html-tags.pipe';
import { SessionStorageService } from '@services/browser-storage/browser-storage.service';
import { HashService } from '@services/hash/hash.service';
import { BehaviorSubject, Observable, catchError, mergeMap, of } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  static readonly ORDER_NOTIFICATIONS_STORAGE_KEY = 'ts-order-notifications';
  static readonly DASHBOARD_NOTIFICATIONS_STORAGE_KEY = 'ts-dashboard-notifications';
  order_notifications: BehaviorSubject<Notification[]> = new BehaviorSubject<Notification[]>([]);
  private strip_html_tags_pipe: StripHtmlTagsPipe;

  constructor(
    private http: HttpClient,
    private storage_svc: SessionStorageService,
    private hash_svc: HashService
  ) {}

  dashboardNotifications(storage_key?: string): Observable<Notification[]> {
    return this.callNotificationsService(
      StoreEndpointsConstant.DASHBOARD_NOTIFICATIONS,
      storage_key
    );
  }

  orderNotifications(order_type_id: number, storage_key?: string, replacement_user_id?: number) {
    return this.callNotificationsService(
      StoreEndpointsConstant.ORDER_NOTIFICATIONS,
      storage_key,
      order_type_id,
      replacement_user_id
    ).subscribe({
      next: (notifications: Notification[]) => this.order_notifications.next(notifications),
      error: (err: any) => {}
    });
  }

  markAsRead(notification: Notification, storage_key?: string): void {
    if (!storage_key || !notification) return;

    this.hash_svc.digest(notification).subscribe((hash: string | undefined) => {
      if (!hash) return;

      const hashes: string[] = this.storage_svc.getContextItem<string[]>(storage_key) ?? [];
      if (hashes.includes(hash)) return;

      if (hashes.length >= 50) hashes.splice(0, hashes.length - 49);
      hashes.push(hash);

      this.storage_svc.setContextItem(storage_key, hashes);
    });
  }

  clearReadStateForActivity(order_type_id: number): void {
    this.storage_svc.removeContextItems(
      `${NotificationsService.ORDER_NOTIFICATIONS_STORAGE_KEY}-${order_type_id}`
    );
  }

  private callNotificationsService(
    url: string,
    storage_key?: string,
    order_type_id?: number,
    replacement_user_id?: number
  ): Observable<Notification[]> {
    let hashes: string[] | undefined = undefined;
    let param: { [key: string]: number } = {};
    if (storage_key) {
      if (typeof order_type_id === 'number' && !storage_key.endsWith(`-${order_type_id}`))
        storage_key = `${storage_key}-${order_type_id}`;
      hashes = this.storage_svc.getContextItem<string[]>(storage_key);
    }

    if (typeof order_type_id === 'number') {
      param = { orderTypeId: order_type_id };

      if (replacement_user_id) param['replacementUserId'] = replacement_user_id;
    }

    return this.http
      .get<NotificationResponse>(url, {
        params: param
      })
      .pipe(
        catchError(() => of({ notifications: [] } as NotificationResponse)),
        mergeMap((res: NotificationResponse) => {
          const notifications: Notification[] = (res.notifications ?? []).filter(
            (notification: Notification) => !this.isNotificationBlank(notification)
          );

          return hashes?.length && notifications.length
            ? this.hash_svc.filterArrayOutByHashes<Notification>(notifications, hashes)
            : of(notifications);
        })
      );
  }

  private isNotificationBlank(notification: Notification): boolean {
    if (!this.strip_html_tags_pipe) this.strip_html_tags_pipe = new StripHtmlTagsPipe();

    return (
      !this.strip_html_tags_pipe.transform(notification?.notification) &&
      !this.strip_html_tags_pipe.transform(notification?.subject)
    );
  }
}
